import React, { useEffect, useState } from "react";
import { Container, Row, Col, Table, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { createAuthenticatedAxiosInstance, fetchPromotions } from "api/auth";
import SearchAndFilterBar from "components/general/SearchAndFilterBar";
import CustomPagination from "components/general/CustomPagination";
import LoadingScreen from "components/general/LoadingScreen";
import logo from "assets/logo.png";
import {
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SearchTypes } from "types/SearchTypes";
import { useAuthStore } from "../../zustand/loginStore";
import { Promotion } from "interfaces/IPromotion";
import { OrderTypes } from "types/OrdersTypes";

const Promotions: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const axiosInstance = createAuthenticatedAxiosInstance(token);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    created_from: "",
    created_to: "",
  });
  const [sortConfig, setSortConfig] = useState({
    sort_by: "name",
    order: "asc" as OrderTypes,
  });
  const [searchType, setSearchType] = useState<SearchTypes>("text");
  const [searchOptions, setSearchOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [filterBy, setFilterBy] = useState<{
    value: string;
    label: string;
  } | null>(null);

  /**
   * Effect hook to load and update promotions based on filters and pagination
   * @effect
   * @dependencies [currentPage, searchTerm, filters.status, sortConfig]
   */
  useEffect(() => {
    const loadPromotions = async () => {
      try {
        const response = await fetchPromotions(axiosInstance, {
          page: currentPage,
          per_page: 10,
          name: searchTerm,
          status: filters.status,
          sort_by: sortConfig.sort_by,
          order: sortConfig.order,
        });
        setPromotions(response.promotions);
        setTotalPages(response.total_pages);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching promotions:", error);
        setIsLoading(false);
      }
    };

    loadPromotions();
  }, [currentPage, searchTerm, filters.status, sortConfig]);

  /**
   * Navigates to the promotion details page when a row is clicked
   * @param {number} promotionId - The ID of the clicked promotion
   */
  const handleRowClick = (promotionId: number) => {
    navigate(`/promotions/${promotionId}`);
  };

  /**
   * Updates search term and resets pagination
   * @param {string} term - The search term to filter promotions
   */
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  /**
   * Handles sorting configuration changes
   * @param {string} sortBy - The field to sort by
   */
  const handleSort = (sortBy: string) => {
    setSortConfig((prev) => ({
      sort_by: sortBy,
      order: prev.sort_by === sortBy && prev.order === "asc" ? "desc" : "asc",
    }));
    setCurrentPage(1);
  };

  /**
   * Updates the current page number for pagination
   * @param {number} page - The page number to navigate to
   */
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  /**
   * Navigates to the add promotion page
   */
  const handleAddPromotion = () => {
    navigate("/promotions/add");
  };

  /**
   * Handles filter selection changes and updates search type
   * @param {Object | null} selectedOption - The selected filter option
   * @param {string} selectedOption.value - The value of the selected option
   * @param {string} selectedOption.label - The label of the selected option
   * @param {string} [selectedOption.status] - Optional status value for the filter
   */
  const handleFilterChange = (
    selectedOption: { value: string; label: string; status?: string } | null
  ) => {
    if (selectedOption) {
      if (selectedOption.value === "name") {
        setSearchType("text");
        setFilters({ ...filters, status: "" });
      } else {
        setSearchType("status");
        setFilters({ ...filters, status: selectedOption.status || "" });
      }
      setFilterBy(selectedOption);
      setSearchTerm("");
      setCurrentPage(1);
    } else {
      setFilterBy(null);
      setSearchTerm("");
      setFilters({ ...filters, status: "" });
      setCurrentPage(1);
    }
  };

  /**
   * Filter options for the promotions list
   * @constant {Array<{value: string, label: string, status?: string}>}
   */
  const filterOptions = [
    { value: "name", label: "Nombre" },
    { value: "active", label: "Activadas", status: "true" },
    { value: "inactive", label: "Desactivadas", status: "false" },
  ];

  /**
   * Formats a date string into localized dd/mm/yyyy format
   * @function formatDate
   * @param {string} dateString - The date string to format
   * @returns {string} The formatted date string in dd/mm/yyyy format
   *
   * Example:
   * Input: "2024-02-14"
   * Output: "14/02/2024"
   */
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <>
      <Container className="px-4 px-md-5">
        <Row className="justify-content-center mb-4">
          <Col xs={12} className="text-center">
            <img src={logo} alt="Logo" style={{ width: "175px" }} />
          </Col>
        </Row>
        <Row className="mb-4">
          <Col className="text-center">
            <h1>Promociones</h1>
          </Col>
        </Row>

        <SearchAndFilterBar
          searchType={searchType}
          searchTerm={searchTerm}
          onSearchChange={handleSearch}
          searchOptions={searchOptions}
          filterBy={filterBy}
          onFilterChange={handleFilterChange}
          filterOptions={filterOptions}
          isSearchDisabled={!filterBy}
        />

        {isLoading ? (
          <LoadingScreen loadingText="Cargando promociones..." />
        ) : (
          <>
            <div className="table-container mb-5">
              <Table striped bordered hover className="border-radius-10">
                <thead className="bg-primary text-white">
                  <tr>
                    <th
                      className="bg-custom-yellow text-center"
                      onClick={() => handleSort("name")}
                    >
                      Nombre
                      {sortConfig.sort_by === "name" && (
                        <FontAwesomeIcon
                          icon={
                            sortConfig.order === "asc" ? faSortUp : faSortDown
                          }
                          className="ms-2"
                        />
                      )}
                      {sortConfig.sort_by !== "name" && (
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      )}
                    </th>
                    <th
                      className="bg-custom-yellow text-center"
                      onClick={() => handleSort("start_date")}
                    >
                      Fecha Inicio
                      <FontAwesomeIcon
                        icon={
                          sortConfig.sort_by === "start_date"
                            ? sortConfig.order === "asc"
                              ? faSortUp
                              : faSortDown
                            : faSort
                        }
                        className="ms-2"
                      />
                    </th>
                    <th
                      className="bg-custom-yellow text-center"
                      onClick={() => handleSort("end_date")}
                    >
                      Fecha Fin
                      <FontAwesomeIcon
                        icon={
                          sortConfig.sort_by === "end_date"
                            ? sortConfig.order === "asc"
                              ? faSortUp
                              : faSortDown
                            : faSort
                        }
                        className="ms-2"
                      />
                    </th>
                    <th className="bg-custom-yellow text-center">
                      Descripción
                    </th>
                    <th
                      className="bg-custom-yellow text-center"
                      onClick={() => handleSort("status")}
                    >
                      Estado
                      <FontAwesomeIcon
                        icon={
                          sortConfig.sort_by === "status"
                            ? sortConfig.order === "asc"
                              ? faSortUp
                              : faSortDown
                            : faSort
                        }
                        className="ms-2"
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {promotions.map((promotion, index) => (
                    <tr
                      key={promotion.id}
                      className={`custom-row ${
                        index % 2 === 0 ? "odd" : "even"
                      }`}
                      onClick={() => handleRowClick(promotion.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">{promotion.name}</td>
                      <td className="text-center">
                        {formatDate(promotion.start_date)}
                      </td>
                      <td className="text-center">
                        {formatDate(promotion.end_date)}
                      </td>

                      <td className="text-center">{promotion.description}</td>
                      <td className="text-center">
                        <span
                          className={`badge ${
                            promotion.status === 1
                              ? "badge-active"
                              : "badge-inactive"
                          }`}
                        >
                          {promotion.status === 1 ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <Row className="justify-content-center mb-3">
              <Col xs="auto">
                <Button
                  variant="primary"
                  className="fw-bold"
                  onClick={handleAddPromotion}
                >
                  Agregar promoción
                </Button>
              </Col>
            </Row>
            <CustomPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              totalResults={promotions?.length}
            />
          </>
        )}
      </Container>
    </>
  );
};

export default Promotions;
